import { OneEuro } from './OneEuro.js';

/**
 * Convierte los puntos clave de MoveNet en controles del juego. Sin DOM: se prueba en Node.
 *
 * Entrada (update): 17 puntos [x, y, confianza] ya en espejo y en «unidades de altura de imagen»
 * (y de 0 arriba a 1 abajo; x de 0 a ancho/alto). Así tu izquierda es la izquierda de la pantalla.
 *
 * Calibración en dos partes:
 *  1. Quieto 3 s: tu postura de referencia (torso, hombros, rodillas, brazos en reposo).
 *  2. Tus gestos (opcional, se guarda): saltas, te agachas y levantas cada brazo, y se mide cuánto
 *     y a qué velocidad lo haces tú. Los umbrales quedan a una fracción de TU movimiento: basta con
 *     empezar el gesto, y es fiable porque está hecho a tu medida. Sin ella se usa un perfil medio.
 *
 * Detección en cuanto empieza el gesto: cada gesto tiene una amplitud (cuánto se ha movido el
 * cuerpo respecto a la referencia) y una velocidad. Cuenta si la amplitud pasa de una fracción de
 * la tuya (kA) o, antes aún, si ya pasa de una fracción menor (kE) moviéndose deprisa (kV de tu
 * velocidad máxima). Todo en «torsos» (hombros → caderas): igual de cerca que de lejos.
 *  - saltar: suben hombros Y caderas (al cambiar de distancia las caderas, cerca del centro de la
 *    imagen, apenas se mueven). No se usan los pies: con ropa oscura MoveNet los pierde;
 *  - agacharse: se acorta la distancia vertical hombros → rodillas y bajan las caderas (sin
 *    rodillas a la vista, vale que bajen hombros y caderas). Por encima del «amago» que haces
 *    antes de saltar, medido en la calibración;
 *  - brazo izquierdo / derecho: la muñeca sube respecto a su posición de reposo;
 *  - mientras no haces ningún gesto, la referencia sigue despacio tu postura (ADAPT_TIME).
 */

export const KP = {
  nose: 0, lEye: 1, rEye: 2, lEar: 3, rEar: 4,
  lShoulder: 5, rShoulder: 6, lElbow: 7, rElbow: 8, lWrist: 9, rWrist: 10,
  lHip: 11, rHip: 12, lKnee: 13, rKnee: 14, lAnkle: 15, rAnkle: 16,
};

/** Textos de los avisos de colocación (GestureDetector.guidance). */
export const GUIDANCE_TEXT = {
  ok: 'Bien colocado',
  feet: 'Bien · mejor si se te ven los pies',
  missing: 'No te veo: ponte delante de la cámara',
  closer: 'Acércate un poco',
  farther: 'Aléjate: necesito verte de cuerpo entero',
  light: 'Falta luz',
};

/** Huesos del esqueleto (para dibujarlo). */
export const BONES = [
  [5, 6], [5, 7], [7, 9], [6, 8], [8, 10], [5, 11], [6, 12], [11, 12],
  [11, 13], [13, 15], [12, 14], [14, 16], [0, 1], [0, 2], [1, 3], [2, 4],
];

/** Ajustes del jugador. Las sensibilidades multiplican: 2 = el gesto basta con la mitad de amplitud. */
export const GESTURE_DEFAULTS = {
  minScore: 0.3, // confianza mínima de un punto clave
  jump: 1,
  crouch: 1,
  lateral: 1,
  turnMode: 'arms', // 'arms' (brazo levantado) | 'shoulders' (girar los hombros, experimental)
};

/**
 * Perfil de gestos medio (sin calibrar los gestos). amp: amplitud máxima del gesto en torsos;
 * vel: su velocidad máxima en torsos/s; dip: cuánto se encoge el cuerpo al coger impulso para saltar;
 * drop: bajada de hombros y caderas al agacharse (para cuando no se ven las rodillas).
 */
export const DEFAULT_PROFILE = {
  jump: { amp: 0.45, vel: 3, dip: 0 },
  crouch: { amp: 0.35, vel: 2.5, drop: 0.6 },
  arm: { amp: 1.5, vel: 9 },
};

// Fracciones del gesto del jugador: kA basta siempre; kE basta si va deprisa (kV de su velocidad
// máxima); por debajo de kR se puede repetir el gesto.
const K = {
  jump: { kA: 0.4, kE: 0.2, kV: 0.4, kR: 0.15 },
  crouch: { kA: 0.45, kE: 0.2, kV: 0.45, kR: 0.2 },
  arm: { kA: 0.5, kE: 0.3, kV: 0.4, kR: 0.3 },
};
const MIN_TH = { jump: 0.07, crouch: 0.07, arm: 0.35 }; // umbral mínimo (en torsos), por el temblor
const CROUCH_HIP_DROP = 0.05; // al agacharse las caderas bajan algo (inclinarse hacia delante no)
const DIP_MARGIN = 1.15; // agacharse tiene que superar 1,15 veces el amago previo al salto
const SHOULDER_TURN = 0.72; // ancho aparente de hombros / ancho al calibrar → giro de hombros
const SHOULDER_REARM = 0.85;
const LATERAL_RANGE = 0.8; // desplazamiento del centro del cuerpo para llegar al tope lateral
const LATERAL_DEAD = 0.06;
const COOLDOWN = { jump: 0.35, crouch: 0.4, turn: 0.3 };
const ARM_CONFIRM = 0.04; // s con el brazo arriba y el cuerpo sin subir (al saltar los brazos se balancean)
const AFTER_LANDING_NO_CROUCH = 0.25; // el aterrizaje flexiona las piernas: no es agacharse
const VEL_SMOOTH = 0.55; // suavizado de las velocidades (0–1, más = menos suave)
const ADAPT_TIME = 4; // constante de tiempo (s) con la que la referencia sigue tu postura en reposo
const LOST_AFTER = 0.4; // segundos sin ver el torso → 'lost'
const CALIBRATION_TIME = 3;
const CALIBRATION_TOLERANCE = 0.08; // cuánto (en torsos) se puede mover durante la calibración
const MIN_TORSO = 0.07; // torso más pequeño que esto (en alturas de imagen) → acércate (de cuerpo entero ronda 0,12–0,2)
const DARK = 0.14; // brillo medio de la imagen por debajo del cual avisamos de que falta luz
const ARM_REST = -0.9; // muñeca respecto al hombro con los brazos caídos (si no se ven en la calibración)

/** Pasos de la calibración de gestos. rep: [empieza, termina, mínimo válido] de la señal, en torsos. */
export const TRAIN_STEPS = [
  { gesture: 'jump', prompt: 'Salta', reps: 2, rep: [0.08, 0.04, 0.14] },
  { gesture: 'crouch', prompt: 'Agáchate flexionando las rodillas', reps: 2, rep: [0.06, 0.03, 0.1] },
  { gesture: 'armL', prompt: 'Levanta el brazo izquierdo', reps: 1, rep: [0.45, 0.3, 0.7] },
  { gesture: 'armR', prompt: 'Levanta el brazo derecho', reps: 1, rep: [0.45, 0.3, 0.7] },
];
const TRAIN_STEP_TIMEOUT = 12; // s; si no llega el gesto, se pasa al siguiente con el valor medio
const DIP_WINDOW = 0.6; // s antes de despegar en los que se mide el amago

const mid = (p, a, b, axis) => (p[a * 3 + axis] + p[b * 3 + axis]) / 2;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const copy = (o) => JSON.parse(JSON.stringify(o));

export class GestureDetector {
  constructor(settings = {}, profile = null) {
    this.settings = { ...GESTURE_DEFAULTS, ...settings };
    this.profile = copy(profile ?? DEFAULT_PROFILE);
    this.points = new Float32Array(51);
    // Poco suavizado: el corte sube enseguida con la velocidad (dCutoff alto), sin retrasar los gestos.
    this.filters = Array.from({ length: 34 }, () => new OneEuro(1.5, 20, 4));
    this.aspect = 4 / 3;
    this.brightness = 1;
    this.baseline = null;
    this.calib = null;
    this.training = null;
    this.visible = false;
    this.lastSeen = -Infinity;
    this.armed = { jump: true, crouch: true, left: true, right: true, shoulders: true };
    this.cool = { jump: -Infinity, crouch: -Infinity, turn: -Infinity };
    this.airborne = false; // tras un salto, hasta que el cuerpo vuelve a su altura
    this.landedAt = -Infinity;
    this.armHold = null;
    this.prev = null; // señales del fotograma anterior (para las velocidades)
    this.vel = { rise: 0, squash: 0, drop: 0, armL: 0, armR: 0 };
    this.squashHistory = []; // [t, squash] recientes, para medir el amago antes de saltar
    this.lateral = 0;
    this.metrics = {};
    this.guidance = 'missing';
    this.events = [];
    this.lastT = 0;
  }

  setSettings(s) {
    Object.assign(this.settings, s);
  }

  setProfile(profile) {
    this.profile = copy(profile ?? DEFAULT_PROFILE);
  }

  /** Brillo medio de la imagen (0–1), medido fuera (necesita la imagen; aquí solo llegan puntos). */
  setBrightness(b) {
    this.brightness = b;
  }

  /** Listo para jugar: postura de referencia medida y sin calibración de gestos en curso. */
  get calibrated() {
    return this.baseline !== null && !this.calib && !this.training;
  }

  /** Empieza la calibración: quieto 3 s con el cuerpo a la vista y, si train, después tus gestos. */
  startCalibration({ train = false } = {}) {
    this.calib = { start: null, n: 0, sum: null, arm: 0, armN: 0, progress: 0, train };
    this.training = null;
  }

  /** Salta lo que quede de la calibración de gestos (los gestos no medidos usan el perfil actual). */
  skipTraining(t = this.lastT) {
    if (this.training) this.#finishTraining(t);
  }

  /**
   * Procesa un fotograma. raw: Float32Array(51); t: segundos; aspect: ancho/alto de la imagen.
   * Devuelve los eventos de este fotograma: 'jump' | 'crouch' | 'left' | 'right' | 'lost' | 'found' |
   * 'calibrated' | 'rep' (un gesto de la calibración medido) | 'step' (pasa al siguiente gesto).
   */
  update(raw, t, aspect = this.aspect) {
    this.aspect = aspect;
    this.events = [];
    const p = this.points;
    const min = this.settings.minScore;
    for (let i = 0; i < 17; i++) {
      const score = raw ? raw[i * 3 + 2] : 0;
      p[i * 3 + 2] = score;
      if (score < min) continue; // se queda la última posición buena
      p[i * 3] = this.filters[i * 2].filter(raw[i * 3], t);
      p[i * 3 + 1] = this.filters[i * 2 + 1].filter(raw[i * 3 + 1], t);
    }

    const torsoSeen = [KP.lShoulder, KP.rShoulder, KP.lHip, KP.rHip].every((i) => p[i * 3 + 2] >= min);
    if (torsoSeen) {
      this.lastSeen = t;
      if (!this.visible) {
        this.visible = true;
        this.events.push('found');
      }
    } else if (this.visible && t - this.lastSeen > LOST_AFTER) {
      this.visible = false;
      this.lateral = 0;
      this.prev = null;
      this.events.push('lost');
    }

    this.guidance = this.#guidance(torsoSeen);
    if (this.training && t - this.training.stepStart > TRAIN_STEP_TIMEOUT) this.#nextStep(t);
    if (!torsoSeen) return this.events;

    const m = this.#measure(t);
    this.metrics = m;
    if (this.calib) this.#calibrate(m, t);
    else if (this.baseline) {
      const sig = this.#signals(m, t);
      if (this.training) this.#train(sig, t);
      else this.#gestures(m, sig, t);
    }
    return this.events;
  }

  /** Consejo para colocarse: 'ok' | 'feet' | 'missing' | 'closer' | 'farther' | 'light'. */
  #guidance(torsoSeen) {
    const p = this.points;
    const min = this.settings.minScore;
    const seen = (i) => p[i * 3 + 2] >= min;
    if (this.brightness < DARK) return 'light';
    if (!torsoSeen) return 'missing';
    const torso = mid(p, KP.lHip, KP.rHip, 1) - mid(p, KP.lShoulder, KP.rShoulder, 1);
    if (torso < MIN_TORSO) return 'closer';
    const headTop = Math.min(...[KP.nose, KP.lEye, KP.rEye].filter(seen).map((i) => p[i * 3 + 1]), 1);
    if (!seen(KP.lKnee) && !seen(KP.rKnee)) return 'farther';
    if (headTop < 0.03) return 'farther';
    if (!seen(KP.lAnkle) && !seen(KP.rAnkle)) return 'feet';
    return 'ok';
  }

  /** Medidas del cuerpo en este fotograma (posiciones absolutas, en alturas de imagen). */
  #measure(t) {
    const p = this.points;
    const min = this.settings.minScore;
    const shY = mid(p, KP.lShoulder, KP.rShoulder, 1);
    const hipY = mid(p, KP.lHip, KP.rHip, 1);
    const shX = mid(p, KP.lShoulder, KP.rShoulder, 0);
    const hipX = mid(p, KP.lHip, KP.rHip, 0);
    const shW = Math.hypot(p[KP.lShoulder * 3] - p[KP.rShoulder * 3], p[KP.lShoulder * 3 + 1] - p[KP.rShoulder * 3 + 1]);
    const torso = hipY - shY;
    const knees = [KP.lKnee, KP.rKnee].filter((i) => p[i * 3 + 2] >= min);
    const kneeY = knees.length ? knees.reduce((s, i) => s + p[i * 3 + 1], 0) / knees.length : null;
    // Altura de cada muñeca sobre su hombro, en torsos (null si no se ve). Tu brazo izquierdo es el
    // que queda a la izquierda de la imagen en espejo: MoveNet a veces intercambia las etiquetas
    // izquierda/derecha, así que se decide por la posición de los hombros en pantalla.
    const arm = (wrist, shoulder) => (p[wrist * 3 + 2] >= min ? (p[shoulder * 3 + 1] - p[wrist * 3 + 1]) / Math.max(torso, 1e-3) : null);
    const swapped = p[KP.lShoulder * 3] > p[KP.rShoulder * 3];
    const armA = arm(KP.lWrist, KP.lShoulder);
    const armB = arm(KP.rWrist, KP.rShoulder);
    return {
      shY,
      hipY,
      centerX: shX * 0.6 + hipX * 0.4, // inclinarse mueve sobre todo los hombros
      shX,
      torso,
      shW,
      shKnee: kneeY === null ? null : kneeY - shY, // se acorta al agacharse
      armL: swapped ? armB : armA,
      armR: swapped ? armA : armB,
      swapped,
      t,
    };
  }

  #calibrate(m, t) {
    const c = this.calib;
    const ok = this.guidance === 'ok' || this.guidance === 'feet';
    const keys = ['shY', 'hipY', 'centerX', 'torso', 'shW', 'shKnee'];
    // Si se mueve demasiado respecto a la media acumulada, se vuelve a empezar.
    const moved = c.n > 0 && Math.max(
      Math.abs(m.shY - c.sum.shY / c.n),
      Math.abs(m.hipY - c.sum.hipY / c.n),
      Math.abs(m.centerX - c.sum.centerX / c.n),
    ) > CALIBRATION_TOLERANCE * (c.sum.torso / c.n);
    if (!ok || moved || m.shKnee === null) {
      Object.assign(c, { start: null, n: 0, arm: 0, armN: 0, progress: 0 });
      return;
    }
    if (c.start === null) {
      c.start = t;
      c.sum = Object.fromEntries(keys.map((k) => [k, 0]));
    }
    for (const k of keys) c.sum[k] += m[k];
    c.n++;
    for (const a of [m.armL, m.armR]) {
      if (a === null) continue;
      c.arm += a;
      c.armN++;
    }
    c.progress = Math.min(1, (t - c.start) / CALIBRATION_TIME);
    if (c.progress < 1) return;
    this.baseline = Object.fromEntries(keys.map((k) => [k, c.sum[k] / c.n]));
    this.baseline.armRest = c.armN ? Math.min(-0.3, c.arm / c.armN) : ARM_REST;
    this.calib = null;
    this.armed = { jump: true, crouch: true, left: true, right: true, shoulders: true };
    this.prev = null;
    if (c.train) {
      this.training = { step: 0, done: 0, stepStart: t, samples: {}, rep: null };
      this.events.push('step');
    } else this.events.push('calibrated');
  }

  /** Señales de los gestos (en torsos, respecto a la referencia) y sus velocidades (torsos/s). */
  #signals(m, t) {
    const b = this.baseline;
    const torso = b.torso;
    const sig = {
      rise: Math.min(b.hipY - m.hipY, b.shY - m.shY) / torso,
      drop: Math.min(m.hipY - b.hipY, m.shY - b.shY) / torso,
      hipDrop: (m.hipY - b.hipY) / torso,
      squash: m.shKnee === null ? null : 1 - m.shKnee / b.shKnee, // 0 de pie, ~0,3–0,5 en sentadilla
      armL: m.armL === null ? 0 : m.armL - b.armRest, // cuánto ha subido la muñeca desde el reposo
      armR: m.armR === null ? 0 : m.armR - b.armRest,
    };
    const prev = this.prev;
    const dt = prev ? t - prev.t : 0;
    if (dt > 0 && dt < 0.25) {
      for (const k of ['rise', 'drop', 'armL', 'armR', 'squash']) {
        if (sig[k] === null || prev[k] === null) continue;
        this.vel[k] += VEL_SMOOTH * ((sig[k] - prev[k]) / dt - this.vel[k]);
      }
    } else for (const k in this.vel) this.vel[k] = 0;
    this.prev = { ...sig, t };
    sig.vel = this.vel;
    // Amago antes de saltar: lo que se encoge el cuerpo en los instantes previos.
    const h = this.squashHistory;
    h.push([t, sig.squash ?? 0]);
    while (h.length && t - h[0][0] > DIP_WINDOW) h.shift();
    return sig;
  }

  /** ¿Ha empezado el gesto? amp: amplitud actual; vel: velocidad; prof: {amp, vel} del jugador. */
  #onset(kind, amp, vel, prof, sens) {
    const k = K[kind];
    const A = prof.amp / sens;
    const full = Math.max(MIN_TH[kind], k.kA * A);
    const early = Math.max(MIN_TH[kind], k.kE * A);
    return amp > full || (amp > early && vel > k.kV * prof.vel);
  }

  /** Umbrales efectivos de cada gesto (con la sensibilidad aplicada). */
  #limits() {
    const s = this.settings;
    const pr = this.profile;
    const crouchAmp = pr.crouch.amp / s.crouch;
    // Agacharse tiene que superar el amago del salto, sin pasar del 80 % de tu sentadilla.
    const dip = Math.min(pr.jump.dip * DIP_MARGIN, 0.8 * crouchAmp);
    return {
      jump: Math.max(MIN_TH.jump, (K.jump.kA * pr.jump.amp) / s.jump),
      crouch: Math.max(MIN_TH.crouch, K.crouch.kA * crouchAmp, dip),
      crouchEarly: Math.max(MIN_TH.crouch, K.crouch.kE * crouchAmp, dip),
      drop: Math.max(MIN_TH.crouch, (K.crouch.kA * pr.crouch.drop) / s.crouch),
      arm: Math.max(MIN_TH.arm, K.arm.kA * pr.arm.amp),
    };
  }

  #gestures(m, sig, t) {
    const b = this.baseline;
    const s = this.settings;
    const pr = this.profile;
    const v = sig.vel;
    const dt = Math.min(0.2, Math.max(0, t - this.lastT));
    this.lastT = t;
    const torso = b.torso;
    const lim = this.#limits();

    const dx = (m.centerX - b.centerX) / torso;
    const widthRatio = (m.shW / m.torso) / (b.shW / b.torso);
    const facing = (this.points[KP.nose * 3] - m.shX) / torso; // < 0: la cara mira a tu izquierda
    Object.assign(this.metrics, {
      rise: sig.rise, drop: sig.drop, squash: sig.squash ?? 0, dx, armL: sig.armL, armR: sig.armR,
      vRise: v.rise, vSquash: v.squash, vArmL: v.armL, vArmR: v.armR, widthRatio, facing,
    });

    // En reposo la referencia sigue despacio tu postura (te acercas, te alejas, te relajas…).
    const resting = !this.airborne && this.armed.jump && this.armed.crouch && Math.abs(sig.rise) < 0.05 &&
      Math.abs(sig.drop) < 0.08 && (sig.squash ?? 0) < 0.08 && sig.armL < 0.3 && sig.armR < 0.3;
    if (resting && dt > 0) {
      const a = dt / ADAPT_TIME;
      for (const key of ['shY', 'hipY', 'torso', 'shW']) b[key] += (m[key] - b[key]) * a;
      if (m.shKnee !== null) b.shKnee += (m.shKnee - b.shKnee) * a;
    }

    // Lateral continuo, con zona muerta (solo lo muestra la página de prueba).
    const range = LATERAL_RANGE / s.lateral;
    const lat = Math.sign(dx) * Math.max(0, Math.abs(dx) - LATERAL_DEAD) / (range - LATERAL_DEAD);
    this.lateral = clamp(lat, -1, 1);

    // Saltar: en cuanto despega (o un poco antes si sube deprisa).
    const jumping = this.#onset('jump', sig.rise, v.rise, pr.jump, s.jump);
    if (this.armed.jump && jumping && t >= this.cool.jump) {
      this.#fire('jump', t);
      this.armed.jump = false;
      this.airborne = true;
    } else if (sig.rise < K.jump.kR * pr.jump.amp) {
      this.armed.jump = true;
      if (this.airborne) {
        this.airborne = false;
        this.landedAt = t;
      }
    }

    // Agacharse (no al aterrizar de un salto).
    let crouching, rearm;
    if (sig.squash !== null) {
      // Antes de llegar al umbral cuenta si ya encoge y va deprisa, o si hombros y caderas ya han bajado mucho.
      const fast = v.squash > K.crouch.kV * pr.crouch.vel || sig.drop > lim.drop;
      crouching = sig.hipDrop > CROUCH_HIP_DROP && (sig.squash > lim.crouch || (fast && sig.squash > lim.crouchEarly));
      rearm = sig.squash < K.crouch.kR * pr.crouch.amp;
    } else {
      crouching = sig.drop > lim.drop;
      rearm = sig.drop < K.crouch.kR * pr.crouch.drop;
    }
    if (this.armed.crouch && crouching && t >= this.cool.crouch && !this.airborne && t - this.landedAt > AFTER_LANDING_NO_CROUCH) {
      this.#fire('crouch', t);
      this.armed.crouch = false;
    } else if (rearm) this.armed.crouch = true;

    // Brazo izquierdo / derecho (en el juego: carril o giro). Al saltar los brazos se balancean
    // (los dos a la vez, y mientras sube el cuerpo): eso no cuenta.
    const steady = sig.rise < lim.jump * 0.6;
    if (s.turnMode === 'shoulders') {
      if (this.armed.shoulders && widthRatio < SHOULDER_TURN && steady && t >= this.cool.turn) {
        this.#fire(facing < 0 ? 'left' : 'right', t);
        this.armed.shoulders = false;
      } else if (widthRatio > SHOULDER_REARM) this.armed.shoulders = true;
    } else {
      const upL = this.#onset('arm', sig.armL, v.armL, pr.arm, 1);
      const upR = this.#onset('arm', sig.armR, v.armR, pr.arm, 1);
      const quiet = (a) => a < K.arm.kE * pr.arm.amp;
      const side = upL && quiet(sig.armR) ? 'left' : upR && quiet(sig.armL) ? 'right' : null;
      if (side && steady && this.armed[side]) {
        if (this.armHold?.side !== side) this.armHold = { side, since: t };
        else if (t - this.armHold.since >= ARM_CONFIRM && t >= this.cool.turn) {
          this.#fire(side, t);
          this.armed[side] = false;
          this.armHold = null;
        }
      } else this.armHold = null;
      const rearmArm = K.arm.kR * pr.arm.amp;
      if (sig.armL < rearmArm) this.armed.left = true;
      if (sig.armR < rearmArm) this.armed.right = true;
    }
  }

  // ---------- Calibración de gestos ----------

  /** Paso actual de la calibración de gestos, para la pantalla, o null. */
  get trainingStatus() {
    const tr = this.training;
    if (!tr) return null;
    const step = TRAIN_STEPS[tr.step];
    const total = TRAIN_STEPS.reduce((n, s) => n + s.reps, 0);
    const done = TRAIN_STEPS.slice(0, tr.step).reduce((n, s) => n + s.reps, 0) + tr.done;
    return { gesture: step.gesture, prompt: step.prompt, rep: tr.done, reps: step.reps, progress: done / total };
  }

  #train(sig, t) {
    const tr = this.training;
    this.lastT = t;
    const step = TRAIN_STEPS[tr.step];
    const [start, end, valid] = step.rep;
    const g = step.gesture;
    const knees = sig.squash !== null;
    const value = g === 'jump' ? sig.rise : g === 'crouch' ? (knees ? sig.squash : sig.drop / 2) : g === 'armL' ? sig.armL : sig.armR;
    const speed = g === 'jump' ? sig.vel.rise : g === 'crouch' ? (knees ? sig.vel.squash : sig.vel.drop / 2) : g === 'armL' ? sig.vel.armL : sig.vel.armR;
    if (!tr.rep) {
      if (value <= start) return;
      // Empieza un gesto. Para el salto, el amago es lo más encogido de los instantes previos.
      const dip = Math.max(0, ...this.squashHistory.map(([, s]) => s));
      tr.rep = { amp: value, vel: speed, dip, drop: sig.drop };
      return;
    }
    const r = tr.rep;
    r.amp = Math.max(r.amp, value);
    r.vel = Math.max(r.vel, speed);
    r.drop = Math.max(r.drop, sig.drop);
    if (value > end) return;
    tr.rep = null;
    if (r.amp < valid) return; // demasiado pequeño: no cuenta
    (tr.samples[g] ??= []).push(r);
    tr.done++;
    this.events.push('rep');
    if (tr.done >= step.reps) this.#nextStep(t);
  }

  #nextStep(t) {
    const tr = this.training;
    tr.step++;
    tr.done = 0;
    tr.rep = null;
    tr.stepStart = t;
    if (tr.step >= TRAIN_STEPS.length) this.#finishTraining(t);
    else this.events.push('step');
  }

  #finishTraining(t) {
    const S = this.training.samples;
    const pr = this.profile;
    const avg = (list, key) => list.reduce((n, r) => n + r[key], 0) / list.length;
    if (S.jump) {
      pr.jump.amp = clamp(avg(S.jump, 'amp'), 0.15, 1.5);
      pr.jump.vel = clamp(avg(S.jump, 'vel'), 0.8, 10);
      pr.jump.dip = clamp(Math.max(...S.jump.map((r) => r.dip)), 0, 0.5);
    }
    if (S.crouch) {
      pr.crouch.amp = clamp(avg(S.crouch, 'amp'), 0.1, 0.7);
      pr.crouch.vel = clamp(avg(S.crouch, 'vel'), 0.5, 8);
      pr.crouch.drop = clamp(avg(S.crouch, 'drop'), 0.2, 1.5);
    }
    const arms = [...(S.armL ?? []), ...(S.armR ?? [])];
    if (arms.length) {
      pr.arm.amp = clamp(avg(arms, 'amp'), 0.7, 2.5);
      pr.arm.vel = clamp(avg(arms, 'vel'), 2, 25);
    }
    this.trained = Object.keys(S).length > 0;
    this.training = null;
    // Lo último de la calibración (el brazo derecho) todavía está bajando: no cuenta como gesto.
    this.armed = { jump: false, crouch: false, left: false, right: false, shoulders: true };
    this.events.push('calibrated');
  }

  #fire(type, t) {
    this.events.push(type);
    if (type === 'jump') this.cool.jump = t + COOLDOWN.jump;
    else if (type === 'crouch') this.cool.crouch = t + COOLDOWN.crouch;
    else this.cool.turn = t + COOLDOWN.turn;
  }

  /** Umbrales efectivos (con la sensibilidad aplicada), para las barras de la página de prueba. */
  get thresholds() {
    const lim = this.#limits();
    return { jump: lim.jump, crouch: lim.crouch, arm: lim.arm, shoulders: SHOULDER_TURN };
  }
}
